import React from 'react';
import { Modal } from '@mantine/core';
import { useAuth0 } from "@auth0/auth0-react";
import classNames from "classnames";
import VehicleHistoryList from './VehicleHistoryList.jsx';

const PageConsumerDetailsAndReporting = ({
    modalShow,
    selectedVehicle,
    selectedVehicleHistory,
    vehicleDriveOffs,
    isLoading,
    driveOffAmount,
    reportedBy,
    handleModalClose,
    handleCheckRegoOnClick,
    handleHistoryButtonClick,
    handleLoadHistoryClick,
    handleMoreHistoryClick,
    handleDriveOffPayClick,
    handleReportDriveOffButtonClick,
    handleReportDriveOffOnClick,
    onDriveOffAmountChange,
    onReportedByChange,
    getDateTimeInfo,
    // historyCount,
    leftPanelView,
    setLeftPanelView
}) => {

    const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();

    if (!selectedVehicle) {
        return null;
    }

    if (!isAuthenticated || user['/roles'].length === 0) {
        return;
    }


    // Helper function to get time only (HH:MM format)
    const getTimeOnly = (dateTimeUtc) => {
        const locale = (navigator && navigator.language) || "en"
        const timeOnly = dateTimeUtc ? new Date(dateTimeUtc).toLocaleString(locale, {
            timeStyle: "short"
        }) : null 
        return timeOnly;
    }

    // Get header background color based on risk level
    const alertStatusClassNames = classNames({
        'red-alert-status': selectedVehicle.alertStatus === 'Red',
        'green-alert-status': selectedVehicle.alertStatus === 'Green',
        'amber-alert-status': selectedVehicle.alertStatus === 'Amber',
        'neutral-alert-status': selectedVehicle.alertStatus === 'Neutral'
    })

    // Get header background color based on risk level
    // const getHeaderBackgroundColor = () => {
    //     if (selectedVehicle.plate === 'unknown') {
    //         return '#DC2626'; // Red for unknown plates (high risk)
    //     } else if (historyCount > 5) {
    //         return '#16A34A'; // Green for >5 visits (low risk)
    //     } else {
    //         return '#0F172A'; // Dark blue for ≤5 visits (medium risk)
    //     }
    // }

    // Get header font weight based on risk level
    const getHeaderFontWeight = () => {
        if (selectedVehicle.plate === 'unknown') {
            return 'normal';
        } else if (selectedVehicle.historyCount >= 5) {
            return 'bold';
        } else {
            return 'normal';
        }
    }

    const formatCurrency = (value, locale = 'en-US', currency = 'AUD') => {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
        }).format(value);
    };

    const remainingHistoryRows = selectedVehicleHistory.length % process.env.REACT_APP_VEHICLE_HISTORY_BATCH_SIZE;

    const loadMoreHistoryButton = selectedVehicleHistory.length !== 0 && remainingHistoryRows === 0 ?
        (<li className="list-group-item text-end">
            <button type="button" className="btn btn-outline-success btn-sm" onClick={() => handleMoreHistoryClick()}>More...</button>
        </li>) : null

    const driveOffTotal = vehicleDriveOffs.reduce((accumulator, currentValue) => {
        return accumulator + currentValue.driveOffAmount;
    }, 0);
    
    const driveOffs = vehicleDriveOffs.length ?
        (
            <div>
                {/* Individual drive-off cards - most recent first */}
                {vehicleDriveOffs.map((driveOff, index) => (
                    <div key={driveOff.currentRecordId} className="card border-danger mb-2">
                        <div className="card-header bg-danger text-white py-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold">
                                    <i className="fa fa-exclamation-circle"></i>
                                </h6>
                                <h5 className="mb-0 fw-bold">{formatCurrency(driveOff.driveOffAmount)}</h5>
                            </div>
                        </div>
                        <div className="card-body p-3">
                            <div className="mb-2">
                                <div className="text-muted" style={{fontSize: '0.75rem'}}>{getDateTimeInfo(driveOff.dateTimeUtc)}</div>
                            </div>
                            <div>
                                <div className="text-muted" style={{fontSize: '0.75rem'}}>Location unknown</div>
                            </div>
                        </div>
                        <div className="card-footer bg-light text-end p-2">
                            <button 
                                type="button" 
                                className="btn btn-success w-100 py-2" 
                                onClick={() => handleDriveOffPayClick()}
                                style={{fontWeight: '500'}}
                            >
                                <i className="fa fa-check-circle me-2"></i>
                                Mark as Paid
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        ) : null;

    const checkRegistrationButton = (
        <button 
            type="button" 
            className="btn btn-primary w-100 py-3" 
            onClick={() => handleCheckRegoOnClick()}
            style={{ fontSize: '1.1rem', fontWeight: '500' }}
        >
            <i className="fa fa-search me-2" style={{ color: 'white' }}></i> CHECK NEVDIS
        </button>
    )

    const loadingSpinner = isLoading ?
        (
            <img className="progress-row-spinner-small" alt="spinner" src="./images/spinner.svg" />
        ) : null;

    const registrationStatus = selectedVehicle.registeredMake !== "NA" ? (
        <React.Fragment>
            <div className="row mt-1 border-top border-bottom">
                <div className="col">
                    {(selectedVehicle.lookupStatus && selectedVehicle.lookupStatus === "NOT FOUND") || (selectedVehicle.registeredMake === "NOT FOUND") ? 
                        (<React.Fragment><p class="text-danger">VEHICLE NOT FOUND</p><div><span className='card-text'>{selectedVehicle.message}</span></div></React.Fragment>) :
                        (<React.Fragment>
                            
                                Vehicle registered as {selectedVehicle.registeredMake} {selectedVehicle.registeredModel} {selectedVehicle.registeredColor}
                                {selectedVehicle.isVehicleStolen ? <p className="text-danger fw-bold">STOLEN VEHICLE</p> : null}
                                <div><span className='card-text'>{selectedVehicle.message}</span></div>
                        </React.Fragment>)}
                </div>
            </div>
        </React.Fragment>
    ) : null

    return (
        <Modal
            opened={modalShow}
            onClose={() => handleModalClose()}
            centered
            size="xl"
            withCloseButton={false}
            padding={0}
        >
            <div
                className={alertStatusClassNames}
                style={{
                    color: 'white',
                    fontWeight: getHeaderFontWeight(),
                    borderBottom: 'none',
                    padding: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <div className="d-flex align-items-center w-100">
                    <span>
                        {getTimeOnly(selectedVehicle.dateTimeUtc)} | {selectedVehicle.plate}  {selectedVehicle.plate === 'unknown' ? null : `| ${selectedVehicle.historyCount} previous visit${selectedVehicle.historyCount !== 1 ? 's' : ''}`}
                    </span>
                    <div className="ms-auto d-flex align-items-center gap-2">
                        {loadingSpinner}
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-light"
                            onClick={() => handleModalClose()}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
            <div className="p-0">
                <div className="container-fluid p-0 modal-content-custom" style={{overflow: 'hidden'}}>
                    <div className="row g-0 app-modal-body-height">
                        {/* LEFT Panel - Dynamic Content */}
                        <div 
                            className="col-md-6 border-end p-0" 
                            style={{ 
                                backgroundColor: '#f8f9fa',
                                overflow: 'hidden',
                                // display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                             }}>
                            {leftPanelView === 'image' && (
                                <div className="d-flex flex-column h-100" style={{ padding: '0' }}>
                                    {/* Drive-Offs Panel - Shows above image if there are unpaid drive-offs */}
                                    {vehicleDriveOffs.length > 0 && (
                                        <div className="p-3 pb-0">
                                            {driveOffs}
                                        </div>
                                    )}
                                    
                                    {/* Vehicle Image */}
                                    {/* <div className="flex-grow-1" style={{ padding: '0', margin: '0', lineHeight: '0' }}> */}
                                    <div class="d-flex align-items-center justify-content-center h-100">
                                        <img 
                                            src={selectedVehicle.vehicleImageUrl} 
                                            alt="Vehicle"
                                            onClick={() => window.open(selectedVehicle.detectionPlateImageUrl, '_blank')}
                                            style={{ 
                                                height: '100%', 
                                                width: 'auto', 
                                                objectFit: 'cover', 
                                                display: 'block',
                                                // margin: '0',
                                                // padding: '0',
                                                cursor: 'pointer'
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {leftPanelView === 'checkResults' && (
                                <div className="p-3">
                                    {/* Back Button */}
                                    <button 
                                        className="btn btn-outline-secondary btn-sm mb-3"
                                        onClick={() => setLeftPanelView('image')}
                                    >
                                        <i className="fa fa-arrow-left me-2"></i>Back to Image
                                    </button>

                                    {/* NAVDIS Check Results */}
                                    <div>
                                        <h5 className="mb-3 fw-bold">NEVDIS Registration Check</h5>
                                        
                                        {isLoading ? (
                                            <div className="text-center py-4">
                                                <div className="spinner-border text-primary mb-2" role="status" style={{width: '2rem', height: '2rem'}}>
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <p className="text-muted mb-0 small">Checking registration...</p>
                                            </div>
                                        ) : (
                                            <div>
                                                {selectedVehicle.lookupStatus === "NOT FOUND" ? (
                                                    <div className="alert alert-danger" role="alert">
                                                        <h6 className="alert-heading fw-bold">
                                                            <i className="fa fa-exclamation-triangle me-2"></i>
                                                            VEHICLE NOT FOUND
                                                        </h6>
                                                        <p className="mb-0 small">This vehicle registration could not be found in the NEVDIS database.</p>
                                                    </div>
                                                ) : selectedVehicle.registeredMake !== "NA" ? (
                                                    <div>
                                                        {/* Stolen Vehicle Warning */}
                                                        {selectedVehicle.isVehicleStolen && (
                                                            <div className="alert alert-danger mb-3" role="alert">
                                                                <h6 className="alert-heading fw-bold">
                                                                    <i className="fa fa-exclamation-circle me-2"></i>
                                                                    ⚠️ STOLEN VEHICLE ⚠️
                                                                </h6>
                                                                <p className="mb-0 small">This vehicle has been reported as stolen. Contact authorities immediately.</p>
                                                            </div>
                                                        )}

                                                        {/* Registration Details */}
                                                        <div className="card">
                                                            <div className="card-header bg-success text-white">
                                                                <h6 className="mb-0 fw-medium">
                                                                    <i className="fa fa-check-circle me-2"></i>
                                                                    Registration Verified
                                                                </h6>
                                                            </div>
                                                            <div className="card-body">
                                                                <div className="row mb-2">
                                                                    <div className="col-5 text-muted small">Plate Number:</div>
                                                                    <div className="col-7 fw-medium">{selectedVehicle.plate}</div>
                                                                </div>
                                                                <div className="row mb-2">
                                                                    <div className="col-5 text-muted small">Make:</div>
                                                                    <div className="col-7 fw-medium">{selectedVehicle.registeredMake}</div>
                                                                </div>
                                                                <div className="row mb-2">
                                                                    <div className="col-5 text-muted small">Model:</div>
                                                                    <div className="col-7 fw-medium">{selectedVehicle.registeredModel}</div>
                                                                </div>
                                                                <div className="row mb-2">
                                                                    <div className="col-5 text-muted small">Color:</div>
                                                                    <div className="col-7 fw-medium">{selectedVehicle.registeredColor}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="alert alert-warning">
                                                        <p className="mb-0 small">Click "Check" button to verify registration.</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {leftPanelView === 'reportForm' && (
                                <div className="p-3">
                                    {/* Back Button */}
                                    <button 
                                        className="btn btn-outline-secondary btn-sm mb-3"
                                        onClick={() => setLeftPanelView('image')}
                                    >
                                        <i className="fa fa-arrow-left me-2"></i>Back to Image
                                    </button>

                                    {/* Report Drive Off Form */}
                                    <div>
                                        <h5 className="mb-3 fw-bold">Report Drive Off</h5>
                                        
                                        {isLoading ? (
                                            <div className="text-center py-4">
                                                <div className="spinner-border text-danger mb-2" role="status" style={{width: '2rem', height: '2rem'}}>
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <p className="text-muted mb-0 small">Submitting report...</p>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="mb-3">
                                                    <label className="form-label small text-muted fw-medium">Amount ($)</label>
                                                    <input 
                                                        type="number" 
                                                        className="form-control form-control-lg" 
                                                        placeholder="Enter amount" 
                                                        value={driveOffAmount}
                                                        onChange={onDriveOffAmountChange}
                                                        min="0"
                                                        step="0.01"
                                                        style={{
                                                            MozAppearance: 'textfield',
                                                            appearance: 'textfield'
                                                        }}
                                                        onWheel={(e) => e.target.blur()}
                                                    />
                                                </div>

                                                <div className="mb-4">
                                                    <label className="form-label small text-muted fw-medium">Reported By</label>
                                                    <input 
                                                        type="text" 
                                                        className="form-control form-control-lg" 
                                                        placeholder={user.name} 
                                                        value={reportedBy}
                                                        onChange={onReportedByChange}
                                                    />
                                                </div>

                                                <button 
                                                    type="button" 
                                                    className="btn btn-danger w-100 py-3" 
                                                    onClick={() => handleReportDriveOffOnClick()}
                                                    style={{ fontSize: '1.1rem', fontWeight: '500' }}
                                                    disabled={!driveOffAmount || !reportedBy}
                                                >
                                                    <i className="fa fa-exclamation-triangle me-2"></i>
                                                    SUBMIT REPORT
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {leftPanelView === 'history' && (
                                <div className="p-3">
                                    {/* Back Button */}
                                    <button 
                                        className="btn btn-outline-secondary btn-sm mb-3"
                                        onClick={() => setLeftPanelView('image')}
                                    >
                                        <i className="fa fa-arrow-left me-2"></i>Back to Image
                                    </button>

                                    {/* History List */}
                                    <div>
                                        <h5 className="mb-3 fw-bold">Vehicle History</h5>
                                        
                                        {isLoading ? (
                                            <div className="text-center py-4">
                                                <div className="spinner-border text-primary mb-2" role="status" style={{width: '2rem', height: '2rem'}}>
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <p className="text-muted mb-0 small">Loading history...</p>
                                            </div>
                                        ) : (
                                            <div>
                                                <ul className="list-group list-group-flush p-1">
                                                    <VehicleHistoryList VehicleHistoryList={selectedVehicleHistory} />
                                                    {loadMoreHistoryButton}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* RIGHT Panel - Split into Top (Plate) and Bottom (Actions) */}
                        <div className="col-md-6 d-flex flex-column" style={{ minHeight: '400px' }}>
                            {/* RIGHT Panel Top - Zoomed in Plate */}
                            <div className="border-bottom" style={{ flex: '0 0 200px', backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {selectedVehicle.regoPlateImageUrl ? (
                                    <img 
                                        src={selectedVehicle.regoPlateImageUrl} 
                                        alt="License Plate"
                                        style={{ width: '100%', maxHeight: '400px', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <p className="text-muted">No Plate Image Available</p>
                                )}
                            </div>

                            {/* RIGHT Panel Bottom - Actions */}
                            <div className="p-3" style={{ flex: '1', overflowY: 'auto', minHeight: '200px' }}>
                                {/* Check Button - Full width, results appear in LEFT panel */}
                                <div className="mb-3">
                                    {checkRegistrationButton}
                                </div>

                                {/* Notes */}
                                {selectedVehicle.notes && (
                                    <div className="mb-3">
                                        <div className="alert alert-info">
                                            <strong>Notes:</strong> {selectedVehicle.notes}
                                        </div>
                                    </div>
                                )}

                                {/* Report Drive Off Button - Opens form in LEFT panel */}
                                <div className="mb-3">
                                    <button 
                                        type="button" 
                                        className="btn btn-danger w-100 py-3" 
                                        onClick={() => handleReportDriveOffButtonClick()}
                                        style={{ fontSize: '1.1rem', fontWeight: '500' }}
                                    >
                                        <i className="fa fa-exclamation-triangle me-2" style={{ color: 'white' }}></i> REPORT DRIVE OFF
                                    </button>
                                </div>

                                {/* History Button - Opens history in LEFT panel */}
                                {selectedVehicle.historyCount > 0 ? 
                                (<div className="mb-3">
                                    <button 
                                        type="button" 
                                        className="btn btn-dark w-100 py-3" 
                                        onClick={() => handleHistoryButtonClick()}
                                        style={{ fontSize: '1.1rem', fontWeight: '500' }}
                                    >
                                        <i className="fa fa-history me-2" style={{ color: 'white' }}></i> HISTORY
                                    </button>
                                </div>) : null
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default PageConsumerDetailsAndReporting;

